// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge } from '@loa/ui-components';
import type { AboutYouSectionContent } from '../../../../types/proposal';

export type SectionAboutYouProps = Readonly<{
  content: AboutYouSectionContent | null | undefined;
}>;

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB');
};

const titleCase = (value: string | undefined): string => {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * ABOUT_YOU section (PRD §3.2 row 1): a read-only facts card per client
 * (auto-resolved from client records; live during DRAFT, frozen into
 * `content.snapshot` once the proposal is sent) plus a dependants table.
 * There is no Save button — this section is never advisor-edited directly.
 */
export function SectionAboutYou({ content }: SectionAboutYouProps) {
  const clients = content?.snapshot?.clients ?? [];
  const dependants = content?.snapshot?.dependants ?? [];

  if (clients.length === 0) {
    return (
      <div className="pension-builder__section" data-testid="section-about-you">
        <p className="pension-builder__empty">No client facts available yet.</p>
      </div>
    );
  }

  return (
    <div className="pension-builder__section" data-testid="section-about-you">
      {clients.map((client) => (
        <div
          key={client.clientId}
          className="pension-builder__facts-card"
          data-testid={`section-about-you-client-${client.clientId}`}
        >
          <h3 className="pension-builder__facts-card-title">{client.name}</h3>
          <dl className="pension-builder__facts-grid">
            <Fact label="Date of Birth" value={formatDate(client.dateOfBirth)} />
            <Fact label="Current Age" value={client.currentAge?.toString() ?? '—'} />
            <Fact label="Marital Status" value={titleCase(client.maritalStatus)} />
            <Fact label="Tax Residency" value={client.taxResidency ?? '—'} />
            <Fact label="Tax Domicile" value={client.taxDomicile ?? '—'} />
            <div className="pension-builder__fact">
              <dt className="pension-builder__fact-label">Attitude To Risk</dt>
              <dd className="pension-builder__fact-value">
                {client.attitudeToRisk ? (
                  <Badge color="orange" size="sm">
                    {client.attitudeToRisk}
                  </Badge>
                ) : (
                  '—'
                )}
              </dd>
            </div>
          </dl>
        </div>
      ))}

      {dependants.length > 0 && (
        <div className="pension-builder__dependants" data-testid="section-about-you-dependants">
          <h3 className="pension-builder__facts-card-title">Dependants</h3>
          <table className="pension-builder__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Relationship</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {dependants.map((dependant, index) => (
                // Dependants have no stable id in the payload — index key is
                // acceptable since the list is a read-only send-time snapshot.
                <tr key={`${dependant.name}-${index}`}>
                  <td>{dependant.name}</td>
                  <td>{dependant.relationship}</td>
                  <td>{dependant.age?.toString() ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Fact({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="pension-builder__fact">
      <dt className="pension-builder__fact-label">{label}</dt>
      <dd className="pension-builder__fact-value">{value}</dd>
    </div>
  );
}

export default SectionAboutYou;
